import type { FeedEvent, FeedEventKind } from '@orgus/protocol';
import {
  Activity,
  ArrowUp,
  Bot,
  ChevronDown,
  CircleCheck,
  CirclePause,
  GitBranch,
  Octagon,
  Play,
  Server,
  ServerOff,
  ShieldAlert,
  SquareTerminal,
  Terminal,
  Wallet,
  Workflow,
} from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { useNavigate } from 'react-router';
import {
  ActivityStream,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  EmptyState,
  Panel,
  Pressable,
  RelativeTime,
  cn,
  formatClock,
  formatInt,
  plural,
  useNow,
} from '@orgus/ui';
import { FEED_LIMIT, useFeed, useOrgus } from '../../lib/store.ts';
import { openNewSession } from '../new-session/store.ts';
import { useSessionParam } from '../sessions/useSessionParam.ts';
import { useFeedTitle } from '../sessions/title.tsx';
import { isOutcome } from './hero.ts';
import { useOverviewPause } from './useHero.ts';
import { FEED_FIRST as FIRST, FEED_PAGE as PAGE, PULSE_KINDS, feedPage, minuteBuckets, seriesOf } from './pulse.ts';

/** The filter's kinds are the stream's series (PULSE_KINDS): the legend and the menu name and count the same things. */
export type FeedFilter = 'outcomes' | 'all' | 'sessions' | 'agents' | 'pipelines' | 'other';
const FILTERS = PULSE_KINDS.map((k) => k.key) as Array<Exclude<FeedFilter, 'all'>>;

export function feedMatches(e: Pick<FeedEvent, 'kind'> & { title?: string }, f: FeedFilter): boolean {
  if (f === 'outcomes') return isOutcome(e);
  return f === 'all' || PULSE_KINDS[seriesOf(e.kind)]?.key === f;
}

export interface MinuteGroup {
  minute: number;
  events: FeedEvent[];
}

/** Newest-first events grouped by the minute they happened in. */
export function groupByMinute(events: FeedEvent[]): MinuteGroup[] {
  const out: MinuteGroup[] = [];
  for (const e of events) {
    const minute = Math.floor(e.at / 60_000);
    const last = out[out.length - 1];
    if (last && last.minute === minute) last.events.push(e);
    else out.push({ minute, events: [e] });
  }
  return out;
}

const ICON: Record<FeedEventKind, ComponentType<{ className?: string }>> = {
  'session.started': Play,
  'session.ended': SquareTerminal,
  'session.waiting': CirclePause,
  'cost.alert': Wallet,
  'security.alert': ShieldAlert,
  'subagent.started': Bot,
  'subagent.finished': Bot,
  'workflow.started': Workflow,
  'workflow.finished': Workflow,
  'pipeline.item': Activity,
  'checkout.changed': GitBranch,
  'machine.online': Server,
  'machine.offline': ServerOff,
  action: CircleCheck,
  job: Terminal,
};

/** Grey unless a person should look: amber for waits and budget alerts, red for failures. */
function tint(e: FeedEvent): string {
  if (e.kind === 'session.waiting' || e.kind === 'cost.alert') return 'text-status-attention';
  if (e.kind === 'security.alert') return 'text-status-danger';
  if ((e.kind === 'pipeline.item' || e.kind === 'job' || e.kind === 'workflow.finished') && /\bfailed\b/i.test(e.title)) return 'text-status-danger';
  return 'text-fg-subtle';
}

const LABEL: Record<FeedFilter, string> = {
  outcomes: 'Outcomes',
  all: 'All activity',
  sessions: 'Sessions',
  agents: 'Agents',
  pipelines: 'Pipelines',
  other: 'Other',
};

const HOUR_MS = 60 * 60_000;

function useDestination() {
  const navigate = useNavigate();
  const [, openSession] = useSessionParam();
  return (e: FeedEvent): (() => void) | null => {
    const r = e.ref;
    if (r?.type === 'session') return () => openSession(r.id);
    if (r?.type === 'pipelineItem') return () => navigate(`/pipelines?project=${encodeURIComponent(e.projectId ?? '')}&item=${encodeURIComponent(r.id)}`);
    if (r?.type === 'job') return () => navigate(`/machines?machine=${encodeURIComponent(e.machineId)}&job=${encodeURIComponent(r.id)}`);
    if (e.kind === 'machine.online' || e.kind === 'machine.offline') return () => navigate(`/machines?machine=${encodeURIComponent(e.machineId)}`);
    const pid = e.projectId;
    if (e.kind === 'checkout.changed' && pid) return () => navigate(`/projects?project=${encodeURIComponent(pid)}`);
    if (e.kind === 'action') return () => navigate('/audit');
    if (e.kind === 'cost.alert') return () => navigate('/spend');
    if (e.kind === 'security.alert') return () => navigate('/security');
    return null;
  };
}

function EventRow({ e, where, go }: { e: FeedEvent; where: string | null; go: (() => void) | null }) {
  const Icon = ICON[e.kind];
  const title = useFeedTitle()(e.title);
  const body = (
    <>
      <span className="relative flex size-6 shrink-0 items-center justify-center rounded-pill bg-surface ring-1 ring-border">
        <Icon className={cn('size-3.5', tint(e))} aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="line-clamp-3 break-words text-ui text-fg" title={title}>
          {title}
        </span>
        <span className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
          <span className="shrink-0" data-meta>
            <RelativeTime ts={e.at} />
          </span>
          {where && (
            <span className="min-w-0 truncate" data-meta>
              {where}
            </span>
          )}
        </span>
      </span>
    </>
  );
  const row = 'flex h-row-2line items-center gap-3 rounded-control px-3';
  // The timeline: one quiet line runs through every event's node, from the first to the last.
  return (
    <li className="motion-arrive relative before:absolute before:inset-y-0 before:left-6 before:w-px before:bg-border-subtle first:before:top-1/2 last:before:bottom-1/2">
      {go ? (
        <Pressable data-row onClick={go} className={cn(row, 'relative transition-colors duration-(--duration-fast) hover:bg-hover')}>
          {body}
        </Pressable>
      ) : (
        <div data-row className={cn(row, 'relative')}>
          {body}
        </div>
      )}
    </li>
  );
}

const STREAM_MINUTES = 60;
/** The chart palette in brand order: blue, cyan, amber and indigo (data-viz.md slots, never status colours). */
const STREAM_SERIES = PULSE_KINDS.map((k, i) => ({ key: k.key, label: k.label, slot: ([1, 5, 2, 8] as const)[i] ?? 8 }));
/** Only events this fresh fly into the stream; older ones (a reconnect catching up) just appear. */
const ARRIVAL_MS = 2 * 60_000;

/** The last hour as a living stream above the events it summarises; each new event drifts in at Now. */
function ActivityPulse({ feed }: { feed: FeedEvent[] }) {
  const now = useNow(10_000);
  const full = feed.length >= FEED_LIMIT;
  const window = useMemo(() => minuteBuckets(feed, now, STREAM_MINUTES, full), [feed, now, full]);
  const total = window.buckets.flat().reduce((a, n) => a + n, 0);
  // A series with nothing in the hour is left out of the stream and its legend (no "Other 0").
  const kept = useMemo(() => STREAM_SERIES.map((_, k) => k).filter((k) => window.buckets.some((b) => (b[k] ?? 0) > 0)), [window]);
  const series = useMemo(() => kept.map((k) => STREAM_SERIES[k]!), [kept]);
  const buckets = useMemo(() => window.buckets.map((b) => kept.map((k) => b[k] ?? 0)), [window, kept]);
  const first = (window.minute - STREAM_MINUTES + 1) * 60_000;
  const arrivals = useMemo(
    () =>
      feed
        .slice(0, 12)
        .filter((e) => e.at > Date.now() - ARRIVAL_MS)
        .map((e) => ({ id: e.id, series: kept.indexOf(seriesOf(e.kind)) }))
        .filter((a) => a.series >= 0)
        .reverse(),
    [feed, kept],
  );
  return (
    <ActivityStream
      className="shrink-0 px-4 pb-3"
      series={series}
      buckets={buckets}
      step={window.minute}
      from={window.from}
      arrivals={arrivals}
      bucketLabel={(i) => formatClock(first + i * 60_000)}
      startLabel={window.from > 0 ? `Since ${formatClock(first + window.from * 60_000)}` : '60m ago'}
      endLabel="Now"
      label={`Activity in the last hour: ${plural(total, 'event')}`}
    />
  );
}

/** The live activity: the last hour as a stream, then every event, newest first, one line each, filterable by kind. */
export function ActivityFeed({ className, bare }: { className?: string; bare?: boolean }) {
  const feed = useFeed();
  const machines = useOrgus((s) => s.machines);
  const projects = useOrgus((s) => s.projects);
  // Outcomes first (submitted, landed, failed, needs you); All activity keeps every event.
  const [filter, setFilter] = useState<FeedFilter>('outcomes');
  const paused = useOverviewPause((s) => s.paused);
  // Paused: the list holds still where it is; new events wait behind the "N new events" button.
  useEffect(() => setHeld(paused ? useOrgus.getState().feed : null), [paused]);
  const [limit, setLimit] = useState(FIRST);
  // While the pointer is over the list, hold it still so a row does not move under the cursor.
  const [held, setHeld] = useState<FeedEvent[] | null>(null);
  const destination = useDestination();
  const manyProjects = Object.keys(projects).length > 1;
  const manyMachines = Object.keys(machines).length > 1;

  const hourAgo = useNow(60_000) - HOUR_MS;
  // Counted over the hour the panel names (its sub, and the stream's legend), so both agree.
  const counts = useMemo(() => {
    const hour = feed.filter((e) => e.at > hourAgo);
    const c = { all: hour.length, outcomes: hour.filter(isOutcome).length } as Record<FeedFilter, number>;
    for (const k of FILTERS) c[k] = hour.filter((e) => feedMatches(e, k)).length;
    return c;
  }, [feed, hourAgo]);
  const source = held ?? feed;
  const filtered = useMemo(() => source.filter((e) => feedMatches(e, filter)), [source, filter]);
  const inHour = useMemo(() => filtered.filter((e) => e.at > hourAgo).length, [filtered, hourAgo]);
  const page = feedPage(inHour, filtered.length, limit);
  const shown = filtered.slice(0, page.show);
  const fresh = held ? feed.filter((e) => feedMatches(e, filter) && e.at > (held[0]?.at ?? 0)).length : 0;
  const whereOf = (e: FeedEvent): string | null => {
    const project = e.projectId ? projects[e.projectId]?.name : undefined;
    if (manyProjects && project) return project;
    // Events the hub raises itself (the landing queue) carry machineId 'hub'.
    if (manyMachines) return e.machineId === 'hub' ? 'Hub' : (machines[e.machineId]?.name ?? null);
    return null;
  };

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" iconRight={<ChevronDown />} aria-label={`Show ${LABEL[filter].toLowerCase()}`}>
          {LABEL[filter]}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={filter} onValueChange={(v) => {
          setFilter(v as FeedFilter);
          setLimit(FIRST);
        }}>
          {/* Like the legend: a kind with nothing in the hour is not offered (unless it is the one chosen). */}
          {(Object.keys(LABEL) as FeedFilter[]).filter((f) => f === 'all' || f === 'outcomes' || f === filter || counts[f] > 0).map((f) => (
            <DropdownMenuRadioItem key={f} value={f}>
              <span className="flex-1">{LABEL[f]}</span>
              <span className="pl-4 text-caption text-fg-subtle num">{formatInt(counts[f])}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    bare ? (
      // Inside the Overview's live frame: a heading row, no panel of its own.
      <section aria-label="Activity" className={cn('flex min-w-0 flex-col', className)}>
        <div className="flex min-w-0 items-center justify-between gap-3 pb-2">
          <h2 className="text-caption text-fg-subtle">Activity</h2>
          {menu}
        </div>
      {feed.length > 0 && filter !== 'outcomes' && <ActivityPulse feed={feed} />}
      {/* In the tall column beside the flow the events scroll inside, under the stream. */}
      <div className="min-h-0 flex-1 overflow-y-auto" onPointerEnter={(ev) => ev.pointerType === 'mouse' && setHeld(feed)} onPointerLeave={() => !paused && setHeld(null)}>
        {/* Floats over the held list, so a row never moves under the pointer when events arrive. */}
        {fresh > 0 && (
          <div className="pointer-events-none sticky top-0 z-(--z-sticky) flex h-0 justify-center">
            <Button size="sm" variant="secondary" icon={<ArrowUp />} className="pointer-events-auto shadow-elevation-1" onClick={() => setHeld(feed)}>
              {plural(fresh, 'new event')}
            </Button>
          </div>
        )}
        {shown.length === 0 ? (
          feed.length === 0 ? (
            <EmptyState icon={<Activity />} title="No activity yet" description="Machines connecting, sessions starting, subagents, pipeline moves and actions appear here the moment they happen." action={<Button onClick={() => openNewSession()}>New session</Button>} />
          ) : (
            <EmptyState icon={<Octagon />} title={filter === 'outcomes' ? 'No outcomes yet' : `No ${LABEL[filter].toLowerCase()} activity`} description={filter === 'outcomes' ? 'Changes submitted, landed or failed, and anything that needs you, appear here.' : 'Nothing of this kind has happened recently.'} action={<Button onClick={() => setFilter('all')}>Show all activity</Button>} />
          )
        ) : (
          <div role="log" aria-live="off" aria-label="Activity, newest first">
            <ol className="flex flex-col px-1 pb-1" data-list>
              {shown.map((e) => (
                <EventRow key={e.id} e={e} where={whereOf(e)} go={destination(e)} />
              ))}
            </ol>
          </div>
        )}
        {shown.length > 0 && (
          // Quiet, like the transcript's Show more: more of the hour, folding back, or where it ends.
          <div className="flex items-center justify-center gap-4 pb-3 pt-1">
            {page.rest > 0 && (
              <Pressable onClick={() => setLimit(page.show + PAGE)} className="flex w-auto items-center gap-1 rounded-control text-caption text-fg-subtle hover:text-fg-muted">
                {`Show ${formatInt(Math.min(PAGE, page.rest))} more`}
                <ChevronDown className="size-3" aria-hidden />
              </Pressable>
            )}
            {page.rest === 0 && (page.show > FIRST || filtered.length > page.show) && <span className="text-caption text-fg-subtle">That is the last hour</span>}
            {page.show > FIRST && (
              <Pressable onClick={() => setLimit(FIRST)} className="flex w-auto items-center gap-1 rounded-control text-caption text-fg-subtle hover:text-fg-muted">
                Show less
                <ChevronDown className="size-3 rotate-180" aria-hidden />
              </Pressable>
            )}
          </div>
        )}
      </div>
      </section>
    ) : (
    <Panel icon={<Activity />} iconTone={4} title="Activity" sub={feed.length ? 'Last hour' : undefined} flush className={className} bodyClassName="flex flex-col" actions={menu}>
      {feed.length > 0 && filter !== 'outcomes' && <ActivityPulse feed={feed} />}
      {/* In the tall column beside the flow the events scroll inside, under the stream. */}
      <div className="min-h-0 flex-1 overflow-y-auto" onPointerEnter={(ev) => ev.pointerType === 'mouse' && setHeld(feed)} onPointerLeave={() => !paused && setHeld(null)}>
        {/* Floats over the held list, so a row never moves under the pointer when events arrive. */}
        {fresh > 0 && (
          <div className="pointer-events-none sticky top-0 z-(--z-sticky) flex h-0 justify-center">
            <Button size="sm" variant="secondary" icon={<ArrowUp />} className="pointer-events-auto shadow-elevation-1" onClick={() => setHeld(feed)}>
              {plural(fresh, 'new event')}
            </Button>
          </div>
        )}
        {shown.length === 0 ? (
          feed.length === 0 ? (
            <EmptyState icon={<Activity />} title="No activity yet" description="Machines connecting, sessions starting, subagents, pipeline moves and actions appear here the moment they happen." action={<Button onClick={() => openNewSession()}>New session</Button>} />
          ) : (
            <EmptyState icon={<Octagon />} title={filter === 'outcomes' ? 'No outcomes yet' : `No ${LABEL[filter].toLowerCase()} activity`} description={filter === 'outcomes' ? 'Changes submitted, landed or failed, and anything that needs you, appear here.' : 'Nothing of this kind has happened recently.'} action={<Button onClick={() => setFilter('all')}>Show all activity</Button>} />
          )
        ) : (
          <div role="log" aria-live="off" aria-label="Activity, newest first">
            <ol className="flex flex-col px-1 pb-1" data-list>
              {shown.map((e) => (
                <EventRow key={e.id} e={e} where={whereOf(e)} go={destination(e)} />
              ))}
            </ol>
          </div>
        )}
        {shown.length > 0 && (
          // Quiet, like the transcript's Show more: more of the hour, folding back, or where it ends.
          <div className="flex items-center justify-center gap-4 pb-3 pt-1">
            {page.rest > 0 && (
              <Pressable onClick={() => setLimit(page.show + PAGE)} className="flex w-auto items-center gap-1 rounded-control text-caption text-fg-subtle hover:text-fg-muted">
                {`Show ${formatInt(Math.min(PAGE, page.rest))} more`}
                <ChevronDown className="size-3" aria-hidden />
              </Pressable>
            )}
            {page.rest === 0 && (page.show > FIRST || filtered.length > page.show) && <span className="text-caption text-fg-subtle">That is the last hour</span>}
            {page.show > FIRST && (
              <Pressable onClick={() => setLimit(FIRST)} className="flex w-auto items-center gap-1 rounded-control text-caption text-fg-subtle hover:text-fg-muted">
                Show less
                <ChevronDown className="size-3 rotate-180" aria-hidden />
              </Pressable>
            )}
          </div>
        )}
      </div>
    </Panel>
    )
  );
}
